import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Pipe, EventEmitter, TemplateRef, forwardRef, ContentChild, Input, Output, ViewChild, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { FormGroup, Validators, NG_VALUE_ACCESSOR, NG_VALIDATORS, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { E11InputModule } from '@engineering11/ui-lib/e11-input';
import * as i3 from '@engineering11/ui-lib/e11-input-label';
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label';
import { fromEvent } from 'rxjs';
import { map, filter, debounceTime } from 'rxjs/operators';

class HighlightPipe {
    transform(text, search, searchKeyword) {
        let pattern = search.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
        pattern = pattern
            .split(' ')
            .filter((t) => {
            return t.length > 0;
        })
            .join('|');
        const regex = new RegExp(pattern, 'gi');
        if (!search) {
            return text;
        }
        if (searchKeyword) {
            const name = text[searchKeyword].replace(regex, (match) => `<b>${match}</b>`);
            // copy original object
            const text2 = { ...text };
            // set bold value into searchKeyword of copied object
            text2[searchKeyword] = name;
            return text2;
        }
        else {
            return search ? text.replace(regex, (match) => `<b>${match}</b>`) : text;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HighlightPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: HighlightPipe, name: "highlight" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HighlightPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'highlight',
                }]
        }] });

/**
 * Keyboard events
 */
const isArrowUp = (keyCode) => keyCode === 38;
const isArrowDown = (keyCode) => keyCode === 40;
const isArrowUpDown = (keyCode) => isArrowUp(keyCode) || isArrowDown(keyCode);
const isEnter = (keyCode) => keyCode === 13;
const isBackspace = (keyCode) => keyCode === 8;
const isDelete = (keyCode) => keyCode === 46;
const isESC = (keyCode) => keyCode === 27;
const isTab = (keyCode) => keyCode === 9;
class E11AutocompleteComponent {
    constructor(elementRef, renderer) {
        this.renderer = renderer;
        this.query = ''; // search query
        this.filteredList = [];
        this.selectedIdx = -1;
        this.toHighlight = '';
        this.notFound = false;
        this.isFocused = false;
        this.isOpen = false;
        this.isScrollToEnd = false;
        this.overlay = false;
        this.manualOpen = undefined;
        this.manualClose = undefined;
        this.name = '';
        this.label = '';
        this.placeholder = '';
        this.heading = '';
        this.required = false;
        this.data = [];
        this.selectedData = [];
        this.isMultiSelect = false;
        this.notFoundText = 'Not found'; // set custom text when filter returns empty result
        this.skeletonLoading = false;
        this.minQueryLength = 1;
        this.focusFirst = false;
        this.stopSelectEvent = false;
        this.parentForm = new FormGroup({});
        this.hasError = false;
        this.selected = new EventEmitter();
        /** Event that is emitted whenever an input is changed. */
        this.inputChanged = new EventEmitter();
        /** Event that is emitted whenever an input is focused. */
        this.inputFocused = new EventEmitter();
        /** Event that is emitted whenever an input is cleared. */
        this.inputCleared = new EventEmitter();
        /** Event that is emitted when the autocomplete panel is opened. */
        this.opened = new EventEmitter();
        /** Event that is emitted when the autocomplete panel is closed. */
        this.closed = new EventEmitter();
        /** Event that is emitted when scrolled to the end of items. */
        this.scrolledToEnd = new EventEmitter();
        this.propagateChange = () => { };
        this.onTouched = () => { };
        this.elementRef = elementRef;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    isTypeString(item) {
        return typeof item === 'string';
    }
    writeValue(value = '') {
        const query = (value && !this.isTypeString(value) ? value[this.searchKeyword] : value) ?? ''; // Fallback needed for formbuilder to work correctly
        this.query = query.trim();
    }
    /**
     * Registers a handler that is called when something in the view has changed
     */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    onChange(event) {
        this.propagateChange(event.target.value.trim());
    }
    onBlur() {
        this.query = this.query.trim();
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    ngOnInit() {
        this.initFormMethods();
    }
    ngAfterViewInit() {
        this.initEventStream();
        // this.handleScroll();
    }
    setInitialValue(value) {
        if (this.initialValue) {
            this.select(value);
        }
    }
    ngOnChanges(changes) {
        if (changes && changes.hasError)
            return;
        this.setInitialValue(this.initialValue);
        if (changes && changes.data && Array.isArray(changes.data.currentValue)) {
            this.handleItemsChange();
            if (!changes.data.firstChange && this.isFocused) {
                this.handleOpen();
            }
        }
    }
    handleItemsChange() {
        this.isScrollToEnd = false;
        if (!this.isOpen) {
            return;
        }
        this.filteredList = this.data;
        this.notFound = !this.filteredList || this.filteredList.length === 0;
        // Filter list when updating data and panel is open
        if (this.isOpen) {
            this.filterList();
        }
    }
    filterList() {
        this.selectedIdx = -1;
        if (this.query != null && this.data) {
            this.toHighlight = this.query;
            this.filteredList = this.customFilter !== undefined ? this.customFilter([...this.data], this.query) : this.defaultFilterFunction();
            // If [focusFirst]="true" automatically focus the first match
            if (this.filteredList.length > 0 && this.focusFirst) {
                this.selectedIdx = 0;
            }
        }
        else {
            this.notFound = false;
        }
    }
    defaultFilterFunction() {
        return this.data.filter((item) => {
            if (typeof item === 'string') {
                // string logic, check equality of strings
                return (item.toLowerCase().indexOf(this.query.toLowerCase()) > -1 &&
                    this.selectedData.findIndex((selectedValue) => item.toLowerCase() === selectedValue.toLowerCase()) == -1);
            }
            else if (typeof item === 'object' && item instanceof Object) {
                // object logic, check property equality
                return (item[this.searchKeyword].toLowerCase().indexOf(this.query.toLowerCase()) > -1 &&
                    this.selectedData.findIndex((selectedValue) => item[this.searchKeyword].toLowerCase() === selectedValue[this.searchKeyword].toLowerCase()) == -1);
            }
            return;
        });
    }
    select(item) {
        this.query = !this.isMultiSelect ? (!this.isTypeString(item) ? item[this.searchKeyword] : item) : '';
        this.isOpen = true;
        this.overlay = false;
        this.selected.emit(item);
        this.propagateChange(item);
        this.handleClose();
    }
    handleClick(e) {
        let clickedComponent = e.target;
        let inside = false;
        do {
            if (clickedComponent === this.elementRef.nativeElement) {
                inside = true;
                if (this.filteredList.length) {
                    this.handleOpen();
                }
            }
            clickedComponent = clickedComponent.parentNode;
        } while (clickedComponent);
        if (!inside) {
            this.handleClose();
        }
    }
    handleOverlay() {
        this.overlay = false;
    }
    handleScroll() {
        this.renderer.listen(this.filteredListElement.nativeElement, 'scroll', () => {
            this.scrollToEnd();
        });
    }
    setPanelState(event) {
        if (event) {
            event.stopPropagation();
        }
        // If controls are untouched
        if (typeof this.manualOpen === 'undefined' && typeof this.manualClose === 'undefined') {
            this.isOpen = false;
            this.handleOpen();
        }
        // If one of the controls is untouched and other is deactivated
        if ((typeof this.manualOpen === 'undefined' && this.manualClose === false) ||
            (typeof this.manualClose === 'undefined' && this.manualOpen === false)) {
            this.isOpen = false;
            this.handleOpen();
        }
        // if controls are touched but both are deactivated
        if (this.manualOpen === false && this.manualClose === false) {
            this.isOpen = false;
            this.handleOpen();
        }
        // if open control is touched and activated
        if (this.manualOpen) {
            this.isOpen = false;
            this.handleOpen();
            this.manualOpen = false;
        }
        // if close control is touched and activated
        if (this.manualClose) {
            this.isOpen = true;
            this.handleClose();
            this.manualClose = false;
        }
    }
    open() {
        this.manualOpen = true;
        this.isOpen = false;
        this.handleOpen();
    }
    close() {
        this.manualClose = true;
        this.isOpen = true;
        this.handleClose();
    }
    focus() {
        this.handleFocus(event);
    }
    clear() {
        this.remove(event);
    }
    remove(e) {
        e.stopPropagation();
        this.query = '';
        this.inputCleared.emit();
        this.propagateChange(this.query);
        this.setPanelState(e);
        if (this.data && !this.data.length) {
            this.notFound = false;
        }
    }
    handleOpen() {
        if (this.isOpen || (this.isOpen && !this.isLoading)) {
            return;
        }
        // If data exists
        if (this.data && this.data.length) {
            this.isOpen = true;
            this.overlay = true;
            this.filterList();
            this.opened.emit();
        }
    }
    handleClose() {
        if (!this.isOpen) {
            this.isFocused = false;
            return;
        }
        this.isOpen = false;
        this.overlay = false;
        this.filteredList = [];
        this.selectedIdx = -1;
        this.notFound = false;
        this.isFocused = false;
        this.closed.emit();
    }
    handleFocus(e) {
        this.searchInput.nativeElement.focus();
        if (this.isFocused) {
            return;
        }
        this.inputFocused.emit(e);
        // if data exists then open
        if (this.data && this.data.length) {
            this.setPanelState(event);
        }
        this.isFocused = true;
    }
    scrollToEnd() {
        if (this.isScrollToEnd) {
            return;
        }
        const scrollTop = this.filteredListElement.nativeElement.scrollTop;
        const scrollHeight = this.filteredListElement.nativeElement.scrollHeight;
        const elementHeight = this.filteredListElement.nativeElement.clientHeight;
        const atBottom = scrollHeight === scrollTop + elementHeight;
        if (atBottom) {
            this.scrolledToEnd.emit();
            this.isScrollToEnd = true;
        }
    }
    /**
     * Initialize keyboard events
     */
    initEventStream() {
        this.inputKeyUp$ = fromEvent(this.searchInput.nativeElement, 'keyup').pipe(map((e) => e));
        this.inputKeyDown$ = fromEvent(this.searchInput.nativeElement, 'keydown').pipe(map((e) => e));
        this.listenEventStream();
    }
    /**
     * Listen keyboard events
     */
    listenEventStream() {
        // key up event
        this.inputKeyUp$
            .pipe(filter((e) => !isArrowUpDown(e.keyCode) && !isEnter(e.keyCode) && !isESC(e.keyCode) && !isTab(e.keyCode)), debounceTime(this.debounceTime))
            .subscribe((e) => {
            this.onKeyUp(e);
        });
        // cursor up & down
        this.inputKeyDown$.pipe(filter((e) => isArrowUpDown(e.keyCode))).subscribe((e) => {
            e.preventDefault();
            this.onFocusItem(e);
        });
        // enter keyup
        this.inputKeyUp$.pipe(filter((e) => isEnter(e.keyCode))).subscribe((e) => {
            //this.onHandleEnter();
        });
        // enter keydown
        this.inputKeyDown$.pipe(filter((e) => isEnter(e.keyCode))).subscribe((e) => {
            this.onHandleEnter(e);
        });
        // ESC
        this.inputKeyUp$.pipe(filter((e) => isESC(e.keyCode), debounceTime(100))).subscribe((e) => {
            this.onEsc();
        });
        // TAB
        this.inputKeyDown$.pipe(filter((e) => isTab(e.keyCode))).subscribe((e) => {
            this.onTab();
        });
        // delete
        this.inputKeyDown$.pipe(filter((e) => isBackspace(e.keyCode) || isDelete(e.keyCode))).subscribe((e) => {
            this.onDelete();
        });
    }
    /**
     * on keyup == when input changed
     * @param e event
     */
    onKeyUp(e) {
        this.notFound = false; // search results are unknown while typing
        // if input is empty
        if (!this.query) {
            this.notFound = false;
            this.inputChanged.emit(e.target.value);
            this.inputCleared.emit();
            this.setPanelState(e);
        }
        // note that '' can be a valid query
        if (!this.query && this.query !== '') {
            return;
        }
        // if query >= to minQueryLength
        if (this.query.length >= this.minQueryLength) {
            this.inputChanged.emit(e.target.value);
            this.filterList();
            // If no results found
            if (!this.filteredList.length && !this.isLoading) {
                this.notFoundText ? (this.notFound = true) : (this.notFound = false);
            }
            if (this.data && !this.data.length) {
                this.isOpen = true;
            }
        }
    }
    /**
     * Keyboard arrow top and arrow bottom
     * @param e event
     */
    onFocusItem(e) {
        // move arrow up and down on filteredList
        // filteredList
        const totalNumItem = this.filteredList.length;
        if (e.key === 'ArrowDown') {
            let sum = this.selectedIdx;
            sum = this.selectedIdx === null ? 0 : sum + 1;
            this.selectedIdx = (totalNumItem + sum) % totalNumItem;
            this.scrollToFocusedItem(this.selectedIdx);
        }
        else if (e.key === 'ArrowUp') {
            if (this.selectedIdx == -1) {
                this.selectedIdx = 0;
            }
            this.selectedIdx = (totalNumItem + this.selectedIdx - 1) % totalNumItem;
            this.scrollToFocusedItem(this.selectedIdx);
        }
    }
    /**
     * Scroll to focused item
     * * @param index
     */
    scrollToFocusedItem(index) {
        let listElement = null;
        // Define list element
        // filteredList element
        listElement = this.filteredListElement.nativeElement;
        const items = Array.prototype.slice.call(listElement.childNodes).filter((node) => {
            if (node.nodeType === 1) {
                // if node is element
                return node.className.includes('item');
            }
            else {
                return false;
            }
        });
        if (!items.length) {
            return;
        }
        const listHeight = listElement.offsetHeight;
        const itemHeight = items[index].offsetHeight;
        const visibleTop = listElement.scrollTop;
        const visibleBottom = listElement.scrollTop + listHeight - itemHeight;
        const targetPosition = items[index].offsetTop;
        if (targetPosition < visibleTop) {
            listElement.scrollTop = targetPosition;
        }
        if (targetPosition > visibleBottom) {
            listElement.scrollTop = targetPosition - listHeight + itemHeight;
        }
    }
    /**
     * Select item on enter click
     */
    onHandleEnter(e) {
        // click enter to choose item from filteredList or historyList
        if (this.selectedIdx > -1) {
            // filteredList
            this.query = !this.isTypeString(this.filteredList[this.selectedIdx])
                ? this.filteredList[this.selectedIdx][this.searchKeyword]
                : this.filteredList[this.selectedIdx];
            this.select(this.filteredList[this.selectedIdx]);
            if (this.stopSelectEvent)
                e.stopPropagation();
        }
        this.handleClose();
    }
    /**
     * Esc click
     */
    onEsc() {
        this.searchInput.nativeElement.blur();
        this.handleClose();
    }
    /**
     * Tab click
     */
    onTab() {
        this.searchInput.nativeElement.blur();
        this.handleClose();
    }
    /**
     * Delete click
     */
    onDelete() {
        this.isOpen = true;
    }
    validate(control) {
        if (this.required && !control.value && control.touched) {
            this.hasError = true;
            return {
                required: true,
            };
        }
        this.hasError = false;
        return null;
    }
    initFormMethods() {
        if (!this.formControlName || !this.parentForm)
            return;
        const formControl = this.parentForm.controls[this.formControlName];
        if (!formControl)
            return;
        this.required = formControl.hasValidator(Validators.required);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AutocompleteComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11AutocompleteComponent, selector: "e11-autocomplete", inputs: { name: "name", label: "label", placeholder: "placeholder", heading: "heading", required: "required", initialValue: "initialValue", data: "data", selectedData: "selectedData", searchKeyword: "searchKeyword", isMultiSelect: "isMultiSelect", notFoundText: "notFoundText", isLoading: "isLoading", skeletonLoading: "skeletonLoading", debounceTime: "debounceTime", disabled: "disabled", minQueryLength: "minQueryLength", focusFirst: "focusFirst", stopSelectEvent: "stopSelectEvent", customFilter: "customFilter", formControlName: "formControlName", parentForm: "parentForm", hasError: "hasError", itemTemplate: "itemTemplate", notFoundTemplate: "notFoundTemplate" }, outputs: { selected: "selected", inputChanged: "inputChanged", inputFocused: "inputFocused", inputCleared: "inputCleared", opened: "opened", closed: "closed", scrolledToEnd: "scrolledToEnd" }, host: { listeners: { "document:click": "handleClick($event)" }, classAttribute: "ng-autocomplete" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11AutocompleteComponent),
                multi: true,
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => E11AutocompleteComponent),
                multi: true,
            },
        ], queries: [{ propertyName: "customTemplate", first: true, predicate: TemplateRef, descendants: true }], viewQueries: [{ propertyName: "searchInput", first: true, predicate: ["searchInput"], descendants: true }, { propertyName: "filteredListElement", first: true, predicate: ["filteredListElement"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"e11-autocomplete-container\" [ngClass]=\"{ active: isOpen }\">\n  <e11-input-label [for]=\"name\" [label]=\"label\" [disabled]=\"disabled\" [required]=\"required\" [hasError]=\"hasError\"></e11-input-label>\n\n  <div class=\"e11-autocomplete e11-relative input-container\">\n    <input *ngIf=\"skeletonLoading\" class=\"e11-skeleton e11-w-full e11-cursor-wait\" disabled autocomplete=\"off\" />\n    <input\n      #searchInput\n      class=\"e11-border disabled:e11-opacity-50 disabled:e11-cursor-not-allowed hover:e11-shadow\"\n      type=\"text\"\n      [type]=\"!skeletonLoading ? 'text' : 'hidden'\"\n      [name]=\"name\"\n      placeholder=\"{{ placeholder }}\"\n      [(ngModel)]=\"query\"\n      (input)=\"onChange($event)\"\n      (focus)=\"handleFocus($event)\"\n      (blur)=\"onTouched($event); onBlur()\"\n      [disabled]=\"disabled\"\n      autocomplete=\"off\"\n      [ngClass]=\"{\n        'e11-border-skin-grey': !hasError,\n        'e11-border-skin-error e11-text-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error e11-pr-10': hasError,\n        'e11-bg-skin-app-bg focus:e11-ring-0 focus:e11-border-skin-app-borders disabled:e11-border-skin-app-borders': disabled\n      }\"\n    />\n    <div class=\"e11-autocomplete-close\" *ngIf=\"query && !isLoading && !disabled\" (click)=\"remove($event)\">\n      <span class=\"e11-text-skin-light hover:e11-text-skin-base material-icons md-18 e11-align-middle\">close</span>\n    </div>\n    <!--Loading mask-->\n    <div class=\"sk-fading-circle\" *ngIf=\"isLoading\">\n      <div class=\"sk-circle1 sk-circle\"></div>\n      <div class=\"sk-circle2 sk-circle\"></div>\n      <div class=\"sk-circle3 sk-circle\"></div>\n      <div class=\"sk-circle4 sk-circle\"></div>\n      <div class=\"sk-circle5 sk-circle\"></div>\n      <div class=\"sk-circle6 sk-circle\"></div>\n      <div class=\"sk-circle7 sk-circle\"></div>\n      <div class=\"sk-circle8 sk-circle\"></div>\n      <div class=\"sk-circle9 sk-circle\"></div>\n      <div class=\"sk-circle10 sk-circle\"></div>\n      <div class=\"sk-circle11 sk-circle\"></div>\n      <div class=\"sk-circle12 sk-circle\"></div>\n    </div>\n  </div>\n\n  <!--FilteredList items-->\n  <div class=\"suggestions-container e11-bg-white\" [ngClass]=\"{ 'is-hidden': false, 'is-visible': true }\">\n    <!--FilteredList heading-->\n    <div class=\"heading e11-border e11-border-skin-app-borders\" *ngIf=\"filteredList.length > 0 && heading\">\n      <div class=\"text\">{{ heading }}</div>\n    </div>\n\n    <ul #filteredListElement>\n      <li *ngFor=\"let item of filteredList; let idx = index\" class=\"item\">\n        <!--string logic-->\n        <div [class.complete-selected]=\"idx === selectedIdx\" *ngIf=\"isTypeString(item)\" (click)=\"select(item)\">\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { $implicit: item | highlight : toHighlight }\"></ng-container>\n        </div>\n        <!--object logic-->\n        <div [class.complete-selected]=\"idx === selectedIdx\" *ngIf=\"!isTypeString(item)\" (click)=\"select(item)\">\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { $implicit: item | highlight : toHighlight : searchKeyword }\"></ng-container>\n        </div>\n      </li>\n    </ul>\n  </div>\n\n  <!--Not found-->\n  <div class=\"not-found\" *ngIf=\"!isLoading && !skeletonLoading && notFound\">\n    <ng-container *ngTemplateOutlet=\"notFoundTemplate; context: { $implicit: notFoundText }\"></ng-container>\n  </div>\n</div>\n\n<div class=\"e11-autocomplete-overlay\" *ngIf=\"overlay\" (click)=\"handleOverlay()\"></div>\n", styles: ["@import\"https://fonts.googleapis.com/icon?family=Material+Icons\";.ng-autocomplete{width:600px}.e11-autocomplete-container{position:relative;overflow:visible}.e11-autocomplete-container .e11-autocomplete{display:flex;align-items:center}.e11-autocomplete-container .e11-autocomplete .e11-autocomplete-close{position:absolute;right:.5rem;margin:auto;cursor:pointer;top:50%;transform:translateY(-50%);vertical-align:middle}.e11-autocomplete-container>.input-container input{display:block;width:100%;padding:.5rem .75rem;border-radius:2px;height:42px}.e11-autocomplete-container>.suggestions-container{position:absolute;width:100%;height:auto;box-shadow:0 2px 5px #00000040;-webkit-box-sizing:border-box;-moz-box-sizing:border-box;box-sizing:border-box;z-index:999}.e11-autocomplete-container>.suggestions-container ul{padding:0;margin:0;max-height:240px;overflow-y:auto}.e11-autocomplete-container>.suggestions-container ul li{position:relative;list-style:none;padding:0;margin:0;cursor:pointer}.e11-autocomplete-container>.suggestions-container ul li a{padding:14px 15px;display:block;text-decoration:none;color:#333;cursor:pointer;color:#000000de;font-size:15px}.e11-autocomplete-container>.suggestions-container ul li:hover{background-color:#9e9e9e2e}.e11-autocomplete-container>.suggestions-container .complete-selected{background-color:#9e9e9e2e}.e11-autocomplete-container>.suggestions-container .heading{position:relative;padding:10px 15px}.e11-autocomplete-container>.suggestions-container .heading .text{font-size:.85em}.e11-autocomplete-container>.suggestions-container .x{position:absolute;right:10px;margin:auto;cursor:pointer;top:50%;transform:translateY(-50%)}.e11-autocomplete-container>.suggestions-container .x i{color:#0000008a;font-size:18px;vertical-align:middle}.e11-autocomplete-container>.suggestions-container.is-hidden{visibility:hidden}.e11-autocomplete-container>.suggestions-container.is-visible{visibility:visible}.e11-autocomplete-container>.not-found{padding:0 .75em;border:solid 1px #f1f1f1;background:#fff}.e11-autocomplete-container>.not-found div{padding:.4em 0;font-size:.95em;line-height:1.4;border-bottom:1px solid rgba(230,230,230,.7)}.highlight{font-weight:700}.e11-autocomplete-overlay{position:fixed;background-color:transparent;width:100%;height:100%;inset:0;z-index:50}input[type=text]::-ms-clear{display:none}.sk-fading-circle{width:20px;height:20px;position:absolute;right:10px;top:0;bottom:0;margin:auto}.sk-fading-circle .sk-circle{width:100%;height:100%;position:absolute;left:0;top:0}.sk-fading-circle .sk-circle:before{content:\"\";display:block;margin:0 auto;width:15%;height:15%;background-color:#333;border-radius:100%;-webkit-animation:sk-circleFadeDelay 1.2s infinite ease-in-out both;animation:sk-circleFadeDelay 1.2s infinite ease-in-out both}.sk-fading-circle .sk-circle2{-webkit-transform:rotate(30deg);-ms-transform:rotate(30deg);transform:rotate(30deg)}.sk-fading-circle .sk-circle3{-webkit-transform:rotate(60deg);-ms-transform:rotate(60deg);transform:rotate(60deg)}.sk-fading-circle .sk-circle4{-webkit-transform:rotate(90deg);-ms-transform:rotate(90deg);transform:rotate(90deg)}.sk-fading-circle .sk-circle5{-webkit-transform:rotate(120deg);-ms-transform:rotate(120deg);transform:rotate(120deg)}.sk-fading-circle .sk-circle6{-webkit-transform:rotate(150deg);-ms-transform:rotate(150deg);transform:rotate(150deg)}.sk-fading-circle .sk-circle7{-webkit-transform:rotate(180deg);-ms-transform:rotate(180deg);transform:rotate(180deg)}.sk-fading-circle .sk-circle8{-webkit-transform:rotate(210deg);-ms-transform:rotate(210deg);transform:rotate(210deg)}.sk-fading-circle .sk-circle9{-webkit-transform:rotate(240deg);-ms-transform:rotate(240deg);transform:rotate(240deg)}.sk-fading-circle .sk-circle10{-webkit-transform:rotate(270deg);-ms-transform:rotate(270deg);transform:rotate(270deg)}.sk-fading-circle .sk-circle11{-webkit-transform:rotate(300deg);-ms-transform:rotate(300deg);transform:rotate(300deg)}.sk-fading-circle .sk-circle12{-webkit-transform:rotate(330deg);-ms-transform:rotate(330deg);transform:rotate(330deg)}.sk-fading-circle .sk-circle2:before{-webkit-animation-delay:-1.1s;animation-delay:-1.1s}.sk-fading-circle .sk-circle3:before{-webkit-animation-delay:-1s;animation-delay:-1s}.sk-fading-circle .sk-circle4:before{-webkit-animation-delay:-.9s;animation-delay:-.9s}.sk-fading-circle .sk-circle5:before{-webkit-animation-delay:-.8s;animation-delay:-.8s}.sk-fading-circle .sk-circle6:before{-webkit-animation-delay:-.7s;animation-delay:-.7s}.sk-fading-circle .sk-circle7:before{-webkit-animation-delay:-.6s;animation-delay:-.6s}.sk-fading-circle .sk-circle8:before{-webkit-animation-delay:-.5s;animation-delay:-.5s}.sk-fading-circle .sk-circle9:before{-webkit-animation-delay:-.4s;animation-delay:-.4s}.sk-fading-circle .sk-circle10:before{-webkit-animation-delay:-.3s;animation-delay:-.3s}.sk-fading-circle .sk-circle11:before{-webkit-animation-delay:-.2s;animation-delay:-.2s}.sk-fading-circle .sk-circle12:before{-webkit-animation-delay:-.1s;animation-delay:-.1s}@-webkit-keyframes sk-circleFadeDelay{0%,39%,to{opacity:0}40%{opacity:1}}@keyframes sk-circleFadeDelay{0%,39%,to{opacity:0}40%{opacity:1}}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i3.E11InputLabelComponent, selector: "e11-input-label", inputs: ["label", "id", "for", "size", "labelClassOverrides", "required", "hasError", "disabled", "darkMode", "requiredMode", "labelTextOptional", "tooltipConfig"] }, { kind: "pipe", type: HighlightPipe, name: "highlight" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-autocomplete', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11AutocompleteComponent),
                            multi: true,
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => E11AutocompleteComponent),
                            multi: true,
                        },
                    ], encapsulation: ViewEncapsulation.None, host: {
                        '(document:click)': 'handleClick($event)',
                        class: 'ng-autocomplete',
                    }, template: "<div class=\"e11-autocomplete-container\" [ngClass]=\"{ active: isOpen }\">\n  <e11-input-label [for]=\"name\" [label]=\"label\" [disabled]=\"disabled\" [required]=\"required\" [hasError]=\"hasError\"></e11-input-label>\n\n  <div class=\"e11-autocomplete e11-relative input-container\">\n    <input *ngIf=\"skeletonLoading\" class=\"e11-skeleton e11-w-full e11-cursor-wait\" disabled autocomplete=\"off\" />\n    <input\n      #searchInput\n      class=\"e11-border disabled:e11-opacity-50 disabled:e11-cursor-not-allowed hover:e11-shadow\"\n      type=\"text\"\n      [type]=\"!skeletonLoading ? 'text' : 'hidden'\"\n      [name]=\"name\"\n      placeholder=\"{{ placeholder }}\"\n      [(ngModel)]=\"query\"\n      (input)=\"onChange($event)\"\n      (focus)=\"handleFocus($event)\"\n      (blur)=\"onTouched($event); onBlur()\"\n      [disabled]=\"disabled\"\n      autocomplete=\"off\"\n      [ngClass]=\"{\n        'e11-border-skin-grey': !hasError,\n        'e11-border-skin-error e11-text-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error e11-pr-10': hasError,\n        'e11-bg-skin-app-bg focus:e11-ring-0 focus:e11-border-skin-app-borders disabled:e11-border-skin-app-borders': disabled\n      }\"\n    />\n    <div class=\"e11-autocomplete-close\" *ngIf=\"query && !isLoading && !disabled\" (click)=\"remove($event)\">\n      <span class=\"e11-text-skin-light hover:e11-text-skin-base material-icons md-18 e11-align-middle\">close</span>\n    </div>\n    <!--Loading mask-->\n    <div class=\"sk-fading-circle\" *ngIf=\"isLoading\">\n      <div class=\"sk-circle1 sk-circle\"></div>\n      <div class=\"sk-circle2 sk-circle\"></div>\n      <div class=\"sk-circle3 sk-circle\"></div>\n      <div class=\"sk-circle4 sk-circle\"></div>\n      <div class=\"sk-circle5 sk-circle\"></div>\n      <div class=\"sk-circle6 sk-circle\"></div>\n      <div class=\"sk-circle7 sk-circle\"></div>\n      <div class=\"sk-circle8 sk-circle\"></div>\n      <div class=\"sk-circle9 sk-circle\"></div>\n      <div class=\"sk-circle10 sk-circle\"></div>\n      <div class=\"sk-circle11 sk-circle\"></div>\n      <div class=\"sk-circle12 sk-circle\"></div>\n    </div>\n  </div>\n\n  <!--FilteredList items-->\n  <div class=\"suggestions-container e11-bg-white\" [ngClass]=\"{ 'is-hidden': false, 'is-visible': true }\">\n    <!--FilteredList heading-->\n    <div class=\"heading e11-border e11-border-skin-app-borders\" *ngIf=\"filteredList.length > 0 && heading\">\n      <div class=\"text\">{{ heading }}</div>\n    </div>\n\n    <ul #filteredListElement>\n      <li *ngFor=\"let item of filteredList; let idx = index\" class=\"item\">\n        <!--string logic-->\n        <div [class.complete-selected]=\"idx === selectedIdx\" *ngIf=\"isTypeString(item)\" (click)=\"select(item)\">\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { $implicit: item | highlight : toHighlight }\"></ng-container>\n        </div>\n        <!--object logic-->\n        <div [class.complete-selected]=\"idx === selectedIdx\" *ngIf=\"!isTypeString(item)\" (click)=\"select(item)\">\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { $implicit: item | highlight : toHighlight : searchKeyword }\"></ng-container>\n        </div>\n      </li>\n    </ul>\n  </div>\n\n  <!--Not found-->\n  <div class=\"not-found\" *ngIf=\"!isLoading && !skeletonLoading && notFound\">\n    <ng-container *ngTemplateOutlet=\"notFoundTemplate; context: { $implicit: notFoundText }\"></ng-container>\n  </div>\n</div>\n\n<div class=\"e11-autocomplete-overlay\" *ngIf=\"overlay\" (click)=\"handleOverlay()\"></div>\n", styles: ["@import\"https://fonts.googleapis.com/icon?family=Material+Icons\";.ng-autocomplete{width:600px}.e11-autocomplete-container{position:relative;overflow:visible}.e11-autocomplete-container .e11-autocomplete{display:flex;align-items:center}.e11-autocomplete-container .e11-autocomplete .e11-autocomplete-close{position:absolute;right:.5rem;margin:auto;cursor:pointer;top:50%;transform:translateY(-50%);vertical-align:middle}.e11-autocomplete-container>.input-container input{display:block;width:100%;padding:.5rem .75rem;border-radius:2px;height:42px}.e11-autocomplete-container>.suggestions-container{position:absolute;width:100%;height:auto;box-shadow:0 2px 5px #00000040;-webkit-box-sizing:border-box;-moz-box-sizing:border-box;box-sizing:border-box;z-index:999}.e11-autocomplete-container>.suggestions-container ul{padding:0;margin:0;max-height:240px;overflow-y:auto}.e11-autocomplete-container>.suggestions-container ul li{position:relative;list-style:none;padding:0;margin:0;cursor:pointer}.e11-autocomplete-container>.suggestions-container ul li a{padding:14px 15px;display:block;text-decoration:none;color:#333;cursor:pointer;color:#000000de;font-size:15px}.e11-autocomplete-container>.suggestions-container ul li:hover{background-color:#9e9e9e2e}.e11-autocomplete-container>.suggestions-container .complete-selected{background-color:#9e9e9e2e}.e11-autocomplete-container>.suggestions-container .heading{position:relative;padding:10px 15px}.e11-autocomplete-container>.suggestions-container .heading .text{font-size:.85em}.e11-autocomplete-container>.suggestions-container .x{position:absolute;right:10px;margin:auto;cursor:pointer;top:50%;transform:translateY(-50%)}.e11-autocomplete-container>.suggestions-container .x i{color:#0000008a;font-size:18px;vertical-align:middle}.e11-autocomplete-container>.suggestions-container.is-hidden{visibility:hidden}.e11-autocomplete-container>.suggestions-container.is-visible{visibility:visible}.e11-autocomplete-container>.not-found{padding:0 .75em;border:solid 1px #f1f1f1;background:#fff}.e11-autocomplete-container>.not-found div{padding:.4em 0;font-size:.95em;line-height:1.4;border-bottom:1px solid rgba(230,230,230,.7)}.highlight{font-weight:700}.e11-autocomplete-overlay{position:fixed;background-color:transparent;width:100%;height:100%;inset:0;z-index:50}input[type=text]::-ms-clear{display:none}.sk-fading-circle{width:20px;height:20px;position:absolute;right:10px;top:0;bottom:0;margin:auto}.sk-fading-circle .sk-circle{width:100%;height:100%;position:absolute;left:0;top:0}.sk-fading-circle .sk-circle:before{content:\"\";display:block;margin:0 auto;width:15%;height:15%;background-color:#333;border-radius:100%;-webkit-animation:sk-circleFadeDelay 1.2s infinite ease-in-out both;animation:sk-circleFadeDelay 1.2s infinite ease-in-out both}.sk-fading-circle .sk-circle2{-webkit-transform:rotate(30deg);-ms-transform:rotate(30deg);transform:rotate(30deg)}.sk-fading-circle .sk-circle3{-webkit-transform:rotate(60deg);-ms-transform:rotate(60deg);transform:rotate(60deg)}.sk-fading-circle .sk-circle4{-webkit-transform:rotate(90deg);-ms-transform:rotate(90deg);transform:rotate(90deg)}.sk-fading-circle .sk-circle5{-webkit-transform:rotate(120deg);-ms-transform:rotate(120deg);transform:rotate(120deg)}.sk-fading-circle .sk-circle6{-webkit-transform:rotate(150deg);-ms-transform:rotate(150deg);transform:rotate(150deg)}.sk-fading-circle .sk-circle7{-webkit-transform:rotate(180deg);-ms-transform:rotate(180deg);transform:rotate(180deg)}.sk-fading-circle .sk-circle8{-webkit-transform:rotate(210deg);-ms-transform:rotate(210deg);transform:rotate(210deg)}.sk-fading-circle .sk-circle9{-webkit-transform:rotate(240deg);-ms-transform:rotate(240deg);transform:rotate(240deg)}.sk-fading-circle .sk-circle10{-webkit-transform:rotate(270deg);-ms-transform:rotate(270deg);transform:rotate(270deg)}.sk-fading-circle .sk-circle11{-webkit-transform:rotate(300deg);-ms-transform:rotate(300deg);transform:rotate(300deg)}.sk-fading-circle .sk-circle12{-webkit-transform:rotate(330deg);-ms-transform:rotate(330deg);transform:rotate(330deg)}.sk-fading-circle .sk-circle2:before{-webkit-animation-delay:-1.1s;animation-delay:-1.1s}.sk-fading-circle .sk-circle3:before{-webkit-animation-delay:-1s;animation-delay:-1s}.sk-fading-circle .sk-circle4:before{-webkit-animation-delay:-.9s;animation-delay:-.9s}.sk-fading-circle .sk-circle5:before{-webkit-animation-delay:-.8s;animation-delay:-.8s}.sk-fading-circle .sk-circle6:before{-webkit-animation-delay:-.7s;animation-delay:-.7s}.sk-fading-circle .sk-circle7:before{-webkit-animation-delay:-.6s;animation-delay:-.6s}.sk-fading-circle .sk-circle8:before{-webkit-animation-delay:-.5s;animation-delay:-.5s}.sk-fading-circle .sk-circle9:before{-webkit-animation-delay:-.4s;animation-delay:-.4s}.sk-fading-circle .sk-circle10:before{-webkit-animation-delay:-.3s;animation-delay:-.3s}.sk-fading-circle .sk-circle11:before{-webkit-animation-delay:-.2s;animation-delay:-.2s}.sk-fading-circle .sk-circle12:before{-webkit-animation-delay:-.1s;animation-delay:-.1s}@-webkit-keyframes sk-circleFadeDelay{0%,39%,to{opacity:0}40%{opacity:1}}@keyframes sk-circleFadeDelay{0%,39%,to{opacity:0}40%{opacity:1}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { searchInput: [{
                type: ViewChild,
                args: ['searchInput']
            }], filteredListElement: [{
                type: ViewChild,
                args: ['filteredListElement']
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], heading: [{
                type: Input
            }], required: [{
                type: Input
            }], initialValue: [{
                type: Input
            }], data: [{
                type: Input
            }], selectedData: [{
                type: Input
            }], searchKeyword: [{
                type: Input
            }], isMultiSelect: [{
                type: Input
            }], notFoundText: [{
                type: Input
            }], isLoading: [{
                type: Input
            }], skeletonLoading: [{
                type: Input
            }], debounceTime: [{
                type: Input
            }], disabled: [{
                type: Input
            }], minQueryLength: [{
                type: Input
            }], focusFirst: [{
                type: Input
            }], stopSelectEvent: [{
                type: Input
            }], customFilter: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], hasError: [{
                type: Input
            }], selected: [{
                type: Output
            }], inputChanged: [{
                type: Output
            }], inputFocused: [{
                type: Output
            }], inputCleared: [{
                type: Output
            }], opened: [{
                type: Output
            }], closed: [{
                type: Output
            }], scrolledToEnd: [{
                type: Output
            }], itemTemplate: [{
                type: Input
            }], notFoundTemplate: [{
                type: Input
            }], customTemplate: [{
                type: ContentChild,
                args: [TemplateRef]
            }] } });

class E11AutocompleteModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AutocompleteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11AutocompleteModule, declarations: [E11AutocompleteComponent, HighlightPipe], imports: [CommonModule, FormsModule, ReactiveFormsModule, E11InputModule, E11InputLabelModule], exports: [E11AutocompleteComponent, HighlightPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AutocompleteModule, imports: [CommonModule, FormsModule, ReactiveFormsModule, E11InputModule, E11InputLabelModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AutocompleteModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11AutocompleteComponent, HighlightPipe],
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, E11InputModule, E11InputLabelModule],
                    exports: [E11AutocompleteComponent, HighlightPipe],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11AutocompleteComponent, E11AutocompleteModule, HighlightPipe };
